import {
  ContextMenu,
  ContextMenuPopup,
  ContextMenuTrigger,
  DND_DRAGGABLE_PROPS,
  Icon,
  Input,
  Menu,
  MenuPopup,
  MenuTrigger,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  sortableStyle,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
} from "@salestrak/ui";
import { useSortable } from "@dnd-kit/sortable";
import React from "react";

import { EntityTag } from "#app/components/entity";
import {
  CONTEXT_MENU_PARTS,
  DROPDOWN_MENU_PARTS,
  type MenuParts,
} from "#app/components/menus/menu-parts";
import {
  renderWorkflowMenuItems,
  type WorkflowMenuActions,
} from "#app/components/workflows/workflow-menu-items";
import type { WorkspaceWorkflow } from "#app/lib/data";
import type { Omittable } from "@salestrak/ui";

// The sidebar row renders a workflow exactly as `useAppData()` holds it —
// `WorkspaceWorkflow` already carries `visibility`, `viewerAccessLevel` and
// `viewerCanManageSharing`, with the schema's own nullability (R2, R4).

/**
 * The full workflow action menu on a sidebar row. The hosting section wires
 * it ONCE (the actions hook and the dialogs live there, never per row) and
 * the row serves it two ways: a right-click on the row, and the trailing
 * "..." action. Omit it (the settings sidebar) and the row is navigation
 * only.
 */
export type WorkflowItemMenu = Omittable<{
  /** From `useWorkflowMenuActions`, called once by the hosting section. */
  actions: WorkflowMenuActions;
  /** `viewerCanManageSharing` — backend-resolved, never derived. */
  canShare: boolean;
  onShare: () => void;
  onRequestDelete: () => void;
  onRequestCreateWorkflow: () => void;
}>;

/**
 * A workflow row in a sidebar menu. `sortable` makes the whole row a dnd-kit
 * drag source (there is no separate grip: the row itself is the handle, and
 * the grab cursor is its affordance).
 */
export function WorkflowItem({
  workflow,
  isActive,
  onClick,
  menu,
  sortable = false,
}: Omittable<{
  workflow: WorkspaceWorkflow;
  isActive: boolean;
  onClick: () => void;
  menu?: WorkflowItemMenu;
  sortable?: boolean;
}>) {
  const [renaming, setRenaming] = React.useState(false);
  const [draft, setDraft] = React.useState("");
  // Esc must NOT commit: unmounting the focused input fires its blur, whose
  // closure still holds the renaming flag — this is how blur knows the edit
  // was already cancelled.
  const cancelledRef = React.useRef(false);
  // Rename must not start until the opening menu has FINISHED closing: on
  // close Base UI restores focus to its trigger — the row — and if the row
  // has already swapped to the input, that restore lands as the input's
  // blur, which commits the untouched draft and swaps the row straight
  // back. The item click only arms this flag; `onOpenChangeComplete(false)`
  // starts the edit once focus is settled.
  const pendingRenameRef = React.useRef(false);
  // Both menus are controlled so inline Rename starts after close.
  const [contextOpen, setContextOpen] = React.useState(false);
  const [actionsOpen, setActionsOpen] = React.useState(false);

  const {
    // dnd-kit sets role="button" on sortable items; strip it — the row is a
    // list item whose button child owns the role. Its `aria-disabled` goes
    // too: it only means "not draggable", yet a screen reader (and the
    // settings sidebar, which never sorts) announced every row as disabled.
    attributes: { role: _role, "aria-disabled": _ariaDisabled, ...attributes },
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    // The rename input must own its pointer: a text-selection drag inside it
    // would otherwise clear the sensor's activation distance and lift the row.
    disabled: !sortable || renaming,
    id: workflow.workflowId,
  });

  const startRename = () => {
    cancelledRef.current = false;
    setDraft(workflow.name ?? "");
    setRenaming(true);
  };
  const commitRename = () => {
    if (cancelledRef.current) {
      cancelledRef.current = false;
      return;
    }
    const name = draft.trim();
    if (name && name !== workflow.name && menu) {
      void menu.actions.rename(workflow.workflowId, name);
    }
    setRenaming(false);
  };
  const cancelRename = () => {
    cancelledRef.current = true;
    setRenaming(false);
  };

  const handleMenuClosed = (open: boolean) => {
    if (open || !pendingRenameRef.current) return;
    pendingRenameRef.current = false;
    startRename();
  };

  const items = (parts: MenuParts) =>
    menu
      ? renderWorkflowMenuItems({
          parts,
          workflow,
          actions: menu.actions,
          canShare: menu.canShare,
          onShare: menu.onShare,
          onRename: () => {
            pendingRenameRef.current = true;
          },
          onRequestDelete: menu.onRequestDelete,
          onRequestCreateWorkflow: menu.onRequestCreateWorkflow,
          createIssueSource: "workflow_sidebar",
        })
      : null;

  const rowContent = (
    <>
      <EntityTag
        entity={{ id: workflow.workflowId, kind: "workflow", workflow }}
        iconStyle="duotone-rounded"
        interactive={false}
        weight="regular"
      />
      {workflow.visibility === "private" && (
        <Icon color="muted" name="lock" size="sm" />
      )}
    </>
  );

  return (
    <SidebarMenuItem
      ref={setNodeRef}
      style={
        sortable
          ? sortableStyle(transform, {
              transition,
              // The row shifts while its neighbour drags too, so the
              // transition applies whenever the list is sortable.
              isSorting: true,
              isDragging,
              axis: "y",
              dragOpacity: 0.5,
            })
          : undefined
      }
      {...(sortable && !renaming ? DND_DRAGGABLE_PROPS : undefined)}
      // The row is the handle AND the pane's scrollable content, so it keeps
      // panning (the marker's CSS would set `touch-action: none`); the
      // hold-activated touch sensor prevents the scroll once a hold completes.
      className={sortable && !renaming ? "touch-manipulation cursor-grab" : undefined}
      {...attributes}
      {...(renaming ? undefined : listeners)}
    >
      {renaming ? (
        <Input
          aria-label="Workflow name"
          autoFocus
          onBlur={commitRename}
          onChange={(event) => setDraft(event.currentTarget.value)}
          // The current name is the seed AND selected, so typing replaces it
          // while a click-then-edit refines it.
          onFocus={(event) => event.currentTarget.select()}
          onKeyDown={(event) => {
            if (event.key === "Enter") commitRename();
            if (event.key === "Escape") cancelRename();
          }}
          value={draft}
        />
      ) : menu ? (
        <ContextMenu
          open={contextOpen}
          onOpenChange={setContextOpen}
          onOpenChangeComplete={handleMenuClosed}
        >
          {/* Trigger OUTSIDE via render: a context-menu trigger only attaches
            * the right-click listener, so merging it onto the row keeps the
            * row's real <button>. */}
          <ContextMenuTrigger
            render={
              <SidebarMenuButton
                disabled={isDragging}
                isActive={isActive}
                onClick={onClick}
              />
            }
          >
            {rowContent}
          </ContextMenuTrigger>
          <ContextMenuPopup align="start" width="action">
            {items(CONTEXT_MENU_PARTS)}
          </ContextMenuPopup>
        </ContextMenu>
      ) : (
        <SidebarMenuButton
          disabled={isDragging}
          isActive={isActive}
          onClick={onClick}
        >
          {rowContent}
        </SidebarMenuButton>
      )}
      {menu && !renaming && (
        <Menu
          open={actionsOpen}
          onOpenChange={setActionsOpen}
          onOpenChangeComplete={handleMenuClosed}
        >
          <Tooltip>
            <MenuTrigger
              render={
                <TooltipTrigger
                  render={
                    <SidebarMenuAction
                      aria-label="Workflow options"
                      showOnHover
                    />
                  }
                />
              }
            >
              <Icon color="muted" name="ellipsis" size="sm" />
            </MenuTrigger>
            <TooltipPopup side="top">Workflow options</TooltipPopup>
          </Tooltip>
          <MenuPopup align="start" side="right" width="action">
            {items(DROPDOWN_MENU_PARTS)}
          </MenuPopup>
        </Menu>
      )}
    </SidebarMenuItem>
  );
}
